import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MobileLayout from "@/components/MobileLayout";
import OnboardingStep from "@/components/OnboardingStep";
import { useLanguage } from "@/i18n/LanguageProvider";
import LanguageSelector from "@/components/LanguageSelector";
import { ChevronLeft, Globe, Users, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import PrerenderedAvatarPicker from "@/components/avatar/PrerenderedAvatarPicker";
import MetaPersonAvatarFlow from "@/components/avatar/MetaPersonAvatarFlow";
import MetaPersonExternalCreate from "@/components/avatar/MetaPersonExternalCreate";
import MetaPersonImportUpload from "@/components/avatar/MetaPersonImportUpload";
import { supabase } from "@/integrations/supabase/client";
import { invalidateAvatarCache, useAvatar } from "@/hooks/useAvatar";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { toast } from "sonner";
import type { PrerenderedAvatar } from "@/lib/prerenderedAvatars";
import { selectCatalogAvatar } from "@/lib/selectCatalogAvatar";
import { useOnboardingStarted } from "@/hooks/useOnboardingStarted";

// Free-tier onboarding (W4 single-path catalog). Mode order:
//   choose       → catalog primary CTA + skip secondary
//   prerendered  → PrerenderedAvatarPicker → dual-write user_avatars +
//                  user_profiles → /processing
//   external     → instruções de export + abre metaperson.avatarsdk.com fora
//                  do app (Botão 1 dos "dois botões", task 96bc7d74)
//   upload       → Botão 2 "Já tenho meu avatar": sobe o .glb exportado para
//                  staging e a metaperson-import valida (rigRules) e importa
//   generate     → Creator EMBUTIDO (metaperson-token) — gateado por
//                  METAPERSON_CREATE_ENABLED, default false pós-cancelamento
//                  Enterprise
// Avaturn 3-path picker removed W4 (MP Enterprise signed; AvaturnCreator +
// useAvaturn + avaturn-session edge fn deleted in Sub-task C-1).
type Mode = "choose" | "prerendered" | "generate" | "external" | "upload";

function getOnboardingSex(): "male" | "female" | "other" | null {
  try {
    const raw = JSON.parse(localStorage.getItem("vyv_onboarding") || "{}");
    const sex = raw?.sex;
    if (sex === "male" || sex === "female" || sex === "other") return sex;
    return null;
  } catch {
    return null;
  }
}

const OnboardingAvatar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang, setLang } = useLanguage();
  const [mode, setMode] = useState<Mode>("choose");
  const [langOpen, setLangOpen] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const selectingRef = useRef(false);
  const onboardingSex = getOnboardingSex();
  // Gate do Creator EMBUTIDO — flag de CRIAÇÃO, não o METAPERSON_ENABLED de
  // render (separados em 24/08, finding 831ad829: desligar o de render
  // derruba o avatar salvo de 1.556 pessoas).
  const metapersonCreateOn = useFeatureFlag("METAPERSON_CREATE_ENABLED");
  const { canUseMetaPerson } = useAvatar({ preloadModel: false });
  const canGenerate = metapersonCreateOn && canUseMetaPerson;

  // Entry to the avatar step also counts as onboarding_started for the paths
  // that bypass Identity (logged-in-without-avatar Mirror CTA, deep-link,
  // skip/back) — deduped in the hook so the normal Identity -> Avatar flow
  // fires exactly once. EXCEPT a Settings "change avatar" edit, which routes
  // here too: SettingsAvatars threads { state: { mode: 'edit' } } so an existing
  // user re-picking an avatar is NOT counted as starting onboarding.
  const isAvatarEdit =
    (location.state as { mode?: string } | null)?.mode === "edit";
  useOnboardingStarted("avatar", !isAvatarEdit);

  // MetaPerson generate path complete — avatar row is already active (the
  // edge function wrote it + the trigger synced user_profiles). Persist the
  // onboarding metadata and advance. avatar_source='metaperson' satisfies the
  // user_profiles_avatar_source_check constraint.
  const handleGenerateComplete = () => {
    invalidateAvatarCache();
    persistLocal({ avatarSource: "metaperson", avatarType: "metaperson", editEntry: isAvatarEdit });
    goToProcessing();
  };

  const persistLocal = (patch: Record<string, unknown>) => {
    const prev = JSON.parse(localStorage.getItem("vyv_onboarding") || "{}");
    localStorage.setItem(
      "vyv_onboarding",
      JSON.stringify({ ...prev, ...patch }),
    );
  };

  const goToProcessing = () => navigate("/processing");

  // Skip → silhouette fallback. avatar_url stays NULL; avatar_source='none'.
  const skip = () => {
    persistLocal({ avatarSource: "none", avatarType: "placeholder", editEntry: isAvatarEdit });
    goToProcessing();
  };

  // Catalog (MetaPerson prerendered). Dual-write — user_avatars row
  // (canonical, trigger syncs user_profiles.avatar_url/provider) +
  // user_profiles.avatar_source. provider='metaperson' since W4.
  const handlePrerenderedSelect = async (avatar: PrerenderedAvatar) => {
    if (selectingRef.current) return;
    selectingRef.current = true;
    setSelecting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error(t("common.tryAgain"));
        selectingRef.current = false;
        setSelecting(false);
        return;
      }

      await selectCatalogAvatar(user.id, avatar);

      const { error: profileErr } = await supabase
        .from("user_profiles")
        .update({ avatar_source: "pre_rendered" } as never)
        .eq("user_id", user.id);
      if (profileErr) {
        // Soft-fail: the user_avatars row landed, so the user has an avatar.
        // avatar_source is metadata; not blocking onboarding flow.
        console.warn(
          "[OnboardingAvatar] avatar_source update failed:",
          profileErr,
        );
      }

      // Derive sex from the chosen avatar's gender ONLY when the user hasn't set
      // one (onboardingSex === null). The `.is('sex', null)` filter is the
      // authority — anyone who already has a sex (incl. a user deliberately
      // picking a cross-gender avatar, or an existing user editing from Settings)
      // keeps it untouched. Feeds the picker's future-visit default
      // (pickInitialFilter) + the voice resolver's no-avatar fallback.
      // Non-blocking, like the avatar_source update above.
      if (onboardingSex === null) {
        const { error: sexErr } = await supabase
          .from("user_profiles")
          .update({ sex: avatar.gender } as never)
          .eq("user_id", user.id)
          .is("sex", null);
        if (sexErr) {
          console.warn("[OnboardingAvatar] derive-sex update failed:", sexErr);
        }
      }

      invalidateAvatarCache();
      persistLocal({
        avatarSource: "pre_rendered",
        avatarType: "metaperson",
        avatarUrl: avatar.glbUrl,
        editEntry: isAvatarEdit,
        // Keep localStorage coherent so Processing persists the same derived sex
        // (first-time path); no-op when the user already chose a sex.
        ...(onboardingSex === null ? { sex: avatar.gender } : {}),
      });
      goToProcessing();
    } catch (err) {
      console.error("[OnboardingAvatar] prerendered select failed:", err);
      toast.error(t("common.tryAgain"));
      selectingRef.current = false;
      setSelecting(false);
    }
  };

  return (
    <MobileLayout className="py-8">
      {isAvatarEdit && mode === "choose" && (
        <div className="mb-4 pr-24">
          <button
            type="button"
            onClick={() => navigate("/settings/avatars", { replace: true })}
            className="inline-flex min-h-11 items-center gap-1 rounded-lg text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <ChevronLeft size={16} aria-hidden="true" />
            {t("common.back")}
          </button>
        </div>
      )}
      {/* Language selector */}
      <div className="absolute top-4 right-4 z-20">
        <div className="relative">
          <button
            onClick={() => setLangOpen(!langOpen)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-secondary/60 border border-border/40 text-muted-foreground hover:text-foreground transition-colors text-xs"
          >
            <Globe className="w-3.5 h-3.5" />
            <span className="font-display font-medium">{lang}</span>
          </button>
          {langOpen && (
            <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-xl overflow-hidden z-50">
              <LanguageSelector variant="compact-menu" onSelect={() => setLangOpen(false)} />
            </div>
          )}
        </div>
      </div>

      {mode === "choose" && (
        <OnboardingStep
          step={2}
          totalSteps={2}
          title={t("avatar.title")}
          subtitle={t("avatar.subtitle")}
        >
          {/* Premium: generate your own MetaPerson avatar (flag-gated). */}
          {canGenerate && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-6 rounded-2xl flex flex-col gap-4 border border-primary/50 ring-1 ring-primary/20 w-full mb-4"
              style={{
                boxShadow: "0 0 24px rgba(124,58,237,0.15)",
                background:
                  "linear-gradient(135deg, rgba(124,58,237,0.08), rgba(34,211,238,0.03))",
              }}
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground text-base">
                    {t("metaperson.onboarding.title")}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {t("metaperson.onboarding.subtitle")}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMode("generate")}
                className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-display font-semibold text-sm tracking-wide transition-all hover:opacity-90 active:scale-[0.98]"
              >
                {t("metaperson.onboarding.cta")}
              </button>
            </motion.div>
          )}

          {/* Catalog (sole path post-W4). Cyan ring, explicit CTA. */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="glass-card p-6 rounded-2xl flex flex-col gap-4 border border-primary/50 ring-1 ring-primary/20 w-full"
            style={{
              boxShadow: "0 0 24px rgba(0,229,255,0.15)",
              background:
                "linear-gradient(135deg, rgba(34,211,238,0.06), rgba(124,58,237,0.03))",
            }}
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground text-base">
                  {t("avatar.usePrerendered.title")}
                </p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {t("avatar.usePrerendered.subtitle")}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setMode("prerendered")}
              className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-display font-semibold text-sm tracking-wide transition-all hover:opacity-90 active:scale-[0.98]"
            >
              {t("avatar.usePrerendered.cta")}
            </button>
          </motion.div>

          {/* Botão 1 (task 96bc7d74): criar o avatar no SITE da MetaPerson,
              na conta do próprio usuário — sem embed, sem metaperson-token,
              custo marginal zero pro VYV. Sempre visível (o usuário paga o
              próprio export no site). */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-6 rounded-2xl flex flex-col gap-4 border border-border/60 w-full mt-4"
            style={{
              background:
                "linear-gradient(135deg, rgba(124,58,237,0.05), rgba(34,211,238,0.02))",
            }}
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground text-base">
                  {t("metaperson.external.card.title")}
                </p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {t("metaperson.external.card.subtitle")}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setMode("external")}
              className="w-full h-11 rounded-xl font-display font-semibold text-sm tracking-wide transition-all hover:opacity-90 active:scale-[0.98]"
              style={{
                background: "rgba(124,58,237,0.18)",
                border: "0.5px solid rgba(124,58,237,0.4)",
                color: "hsl(255 90% 88%)",
              }}
            >
              {t("metaperson.external.card.createCta")}
            </button>
            {/* Botão 2 (task 96bc7d74): já exportou no site → sobe o .glb. */}
            <button
              type="button"
              onClick={() => setMode("upload")}
              className="w-full h-11 rounded-xl font-display font-semibold text-sm tracking-wide transition-all hover:opacity-90 active:scale-[0.98]"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "0.5px solid rgba(255,255,255,0.14)",
                color: "rgba(255,255,255,0.85)",
              }}
            >
              {t("metaperson.external.card.uploadCta")}
            </button>
          </motion.div>

          {/* Skip — discreet link with explanatory subtitle below. */}
          <div className="flex flex-col items-center mt-3 gap-1">
            <button
              type="button"
              onClick={skip}
              className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
            >
              {t("avatar.skipForNow")}
            </button>
            <p className="text-[11px] text-muted-foreground/60 text-center px-4">
              {t("avatar.skipDescription")}
            </p>
          </div>
        </OnboardingStep>
      )}

      {mode === "prerendered" && (
        <OnboardingStep
          step={2}
          totalSteps={2}
          title={t("avatar.usePrerendered.title")}
          subtitle={t("avatar.usePrerendered.subtitle")}
        >
          <PrerenderedAvatarPicker
            defaultGender={onboardingSex}
            onSelect={handlePrerenderedSelect}
          />
          <button
            onClick={() => setMode("choose")}
            className="text-center text-xs text-muted-foreground/50 mt-4 py-2"
          >
            {t("common.back")}
          </button>
        </OnboardingStep>
      )}

      {mode === "external" && (
        <OnboardingStep
          step={2}
          totalSteps={2}
          title={t("metaperson.external.instructions.title")}
          subtitle={t("metaperson.external.instructions.subtitle")}
        >
          <MetaPersonExternalCreate onBack={() => setMode("choose")} />
        </OnboardingStep>
      )}

      {mode === "upload" && (
        <OnboardingStep
          step={2}
          totalSteps={2}
          title={t("metaperson.upload.title")}
          subtitle={t("metaperson.upload.subtitle")}
        >
          {/* onComplete reaproveita handleGenerateComplete: a linha ativa já
              foi escrita pela edge function (trigger sincronizou o profile),
              então o pós-import é idêntico ao do Creator. */}
          <MetaPersonImportUpload
            onComplete={handleGenerateComplete}
            onBack={() => setMode("choose")}
          />
        </OnboardingStep>
      )}

      {mode === "generate" && canGenerate && (
        <MetaPersonAvatarFlow
          onComplete={handleGenerateComplete}
          onCancel={() => setMode("choose")}
        />
      )}
    </MobileLayout>
  );
};

export default OnboardingAvatar;
